import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../config/axios';
import CustomerLayout from '../../components/Layout/CustomerLayout';
import { useLanguage } from '../../context/LanguageContext';

import { FaTicketAlt, FaChartBar, FaRegCheckCircle, FaFileContract, FaClipboardList, FaHandshake, FaArrowRight, FaHeadset, FaCheckDouble, FaFileSignature, FaClock, FaCheckCircle, FaPlus } from 'react-icons/fa';
import { MdDashboard, MdClose, MdInfo } from 'react-icons/md';
import supportsIcon from '../../assets/images/supports-icon.png';

const CustomerDashboardPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [user, setUser] = useState({
    full_name: '',
    customer_id: ''
  });

  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractProducts, setContractProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSLAs, setProductSLAs] = useState([]);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [stats, setStats] = useState({
    openTickets: 0,
    inProgressTickets: 0,
    closedTickets: 0,
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setUser(userData);
      if (userData.customer_id) {
        fetchContracts(userData.customer_id);
        fetchDashboardStats();
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/customer-dashboard/stats', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchContracts = async (customerId) => {
    try {
      setIsLoading(true);
      const response = await api.get('/customer-contracts', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { customer_id: customerId }
      });
      setContracts(response.data || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchContractProducts = async (contractId) => {
    try {
      setIsLoading(true);
      const response = await api.get('/contract-products', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { contract_id: contractId }
      });
      setContractProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching contract products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductSLAs = async (productId) => {
    try {
      setIsLoading(true);
      const response = await api.get('/sla-definitions', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { product_id: productId }
      });
      setProductSLAs(response.data || []);
    } catch (error) {
      console.error('Error fetching product SLAs:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContractClick = (contract) => {
    setSelectedContract(contract);
    setContractProducts([]);
    setSelectedProduct(null);
    setProductSLAs([]);
    fetchContractProducts(contract.id);
  };
  
  const openContractModal = () => {
      if (contracts.length > 0) {
        handleContractClick(contracts[0]);
      }
      setIsContractModalOpen(true);
  }

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setProductSLAs([]);
    fetchProductSLAs(product.id);
  };

  return (
    <CustomerLayout>
      <main className="container mx-auto py-8 px-4 dark:text-white">
        <div className="bg-gradient-to-r from-[#fff] to-[#fff] dark:from-gray-800 dark:to-gray-800 rounded-xl shadow-lg mb-10 overflow-hidden">
          <div className="flex flex-col md:flex-row items-center">
            <div className="p-8 text-black dark:text-white flex-1">
              <h2 className="text-2xl font-bold mb-3">{t('customerDashboard.welcome')}, {user.full_name || t('customerDashboard.dearCustomer')}!</h2>
              <p className="text-black/80 dark:text-white/0 text- leading-relaxed mb-6">
                {t('customerDashboard.welcomeMessage')}
              </p>
              <button 
                onClick={() => navigate('/customer-support-cases', { state: { openCreateModal: true } })}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors shadow-md"
              >
                <FaPlus size={16} />
                {t('customerDashboard.createNewRequest')}
              </button>
            </div>
            <div className="hidden md:flex items-center justify-center p-8">
              
            </div>
          </div>
        </div>

        {/* İstatistik Kartları - Responsive Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-10">
          <div 
            className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate('/customer-support-cases', { state: { filter: 'OPEN', showPopup: true } })}
          >
            <div className="flex h-full">
              <div className="bg-gradient-to-br from-indigo-200 to-sky-700 p-3 text-white flex flex-col justify-center items-center w-20 sm:w-24 lg:w-1/3">
                <FaHeadset size={20} className="mb-1" />
              </div>
              <div className="p-3 sm:p-4 lg:p-6 flex-1 flex flex-col justify-center">
                <p className="text-gray-500 dark:text-gray-400 text-[10px] sm:text-xs lg:text-sm uppercase font-semibold">{t('customerDashboard.openTickets')}</p>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 dark:text-white mt-0.5">{stats.openTickets}</h3>
              </div>
            </div>
          </div>
          <div 
            className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate('/customer-support-cases', { state: { filter: 'IN PROGRESS', showPopup: true } })}
          >
            <div className="flex h-full">
              <div className="bg-gradient-to-br from-amber-400 to-amber-500 p-3 text-white flex flex-col justify-center items-center w-20 sm:w-24 lg:w-1/3">
                <FaClock size={20} className="mb-1" />
              </div>
              <div className="p-3 sm:p-4 lg:p-6 flex-1 flex flex-col justify-center">
                <p className="text-gray-500 dark:text-gray-400 text-[10px] sm:text-xs lg:text-sm uppercase font-semibold">{t('customerDashboard.inProgressTickets')}</p>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 dark:text-white mt-0.5">{stats.inProgressTickets}</h3>
              </div>
            </div>
          </div>
          <div 
            className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate('/customer-support-cases', { state: { filter: 'CLOSED', showPopup: true } })}
          >
            <div className="flex h-full">
              <div className="bg-gradient-to-br from-green-400 to-green-500 p-3 text-white flex flex-col justify-center items-center w-20 sm:w-24 lg:w-1/3">
                <FaCheckCircle size={20} className="mb-1" />
              </div>
              <div className="p-3 sm:p-4 lg:p-6 flex-1 flex flex-col justify-center">
                <p className="text-gray-500 dark:text-gray-400 text-[10px] sm:text-xs lg:text-sm uppercase font-semibold">{t('customerDashboard.closedTickets')}</p>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 dark:text-white mt-0.5">{stats.closedTickets}</h3>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={openContractModal}>
            <div className="flex h-full">
              <div className="bg-gradient-to-br from-slate-400 to-slate-500 p-3 text-white flex flex-col justify-center items-center w-20 sm:w-24 lg:w-1/3">
                <FaFileSignature size={20} className="mb-1" />
              </div>
              <div className="p-3 sm:p-4 lg:p-6 flex-1 flex flex-col justify-center">
                <p className="text-gray-500 dark:text-gray-400 text-[10px] sm:text-xs lg:text-sm uppercase font-semibold">{t('customerDashboard.contracts')}</p>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 dark:text-white mt-0.5">{contracts.length}</h3>
              </div>
            </div>
          </div>
        </div>

        <h3 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white mb-4 sm:mb-6 px-2">{t('customerDashboard.quickAccess')}</h3>
        {/* Hızlı Erişim Kartları - Responsive Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1 cursor-pointer border border-gray-100 dark:border-gray-700" onClick={() => navigate('/customer-support-cases')}>
            <div className="flex flex-col sm:flex-row h-full">
              <div className="bg-gradient-to-br from-slate-900 to-slate-700 p-3 sm:p-4 lg:p-6 text-white flex flex-col justify-center items-center sm:w-1/3">
                <FaTicketAlt size={24} className="mb-2 sm:mb-3 lg:mb-4 sm:hidden" />
                <FaTicketAlt size={32} className="mb-2 sm:mb-3 lg:mb-4 hidden sm:block lg:hidden" />
                <FaTicketAlt size={40} className="mb-2 sm:mb-3 lg:mb-4 hidden lg:block" />
                <h3 className="text-sm sm:text-lg lg:text-xl font-bold text-center">{t('customerDashboard.mySupportTickets')}</h3>
              </div>
              <div className="p-3 sm:p-4 lg:p-6 flex-1">
                <p className="text-gray-600 dark:text-gray-300 mb-3 sm:mb-4 lg:mb-6 text-sm sm:text-base lg:text-lg">{t('customerDashboard.viewMyTickets')}</p>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-3">
                  <div>
                    <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold mb-1">{t('customerDashboard.openTicketsCount')}</div>
                    <div className="flex items-center"><span className="font-bold text-lg sm:text-xl lg:text-2xl text-blue-600 mr-2">{stats.openTickets}</span><span className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">/ {stats.openTickets + stats.inProgressTickets + stats.closedTickets} {t('customerDashboard.totalTickets')}</span></div>
                  </div>
                  <button className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-lg text-gray-700 dark:text-white text-xs sm:text-sm lg:text-base font-medium transition-colors w-fit"><span>{t('customerDashboard.view')}</span><FaArrowRight size={12} className="sm:hidden" /><FaArrowRight size={14} className="hidden sm:block" /></button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1 cursor-pointer border border-gray-100 dark:border-gray-700" onClick={() => navigate('/customer-reports')}>
            <div className="flex flex-col sm:flex-row h-full">
              <div className="bg-gradient-to-br from-slate-900 to-slate-700 p-3 sm:p-4 lg:p-6 text-white flex flex-col justify-center items-center sm:w-1/3">
                <FaChartBar size={24} className="mb-2 sm:mb-3 lg:mb-4 sm:hidden" />
                <FaChartBar size={32} className="mb-2 sm:mb-3 lg:mb-4 hidden sm:block lg:hidden" />
                <FaChartBar size={40} className="mb-2 sm:mb-3 lg:mb-4 hidden lg:block" />
                <h3 className="text-sm sm:text-lg lg:text-xl font-bold text-center">{t('customerDashboard.reports')}</h3>
              </div>
              <div className="p-3 sm:p-4 lg:p-6 flex-1">
                <p className="text-gray-600 dark:text-gray-300 mb-3 sm:mb-4 lg:mb-6 text-sm sm:text-base lg:text-lg">{t('customerDashboard.createReports')}</p>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-3">
                  <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                    <div className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs">Excel</div>
                    <div className="text-xs bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs">PDF</div>
                    <div className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs">Filtreli</div>
                  </div>
                  <button className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-lg text-gray-700 dark:text-white text-xs sm:text-sm lg:text-base font-medium transition-colors w-fit"><span>{t('customerDashboard.createReport')}</span><FaArrowRight size={12} className="sm:hidden" /><FaArrowRight size={14} className="hidden sm:block" /></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {isContractModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl overflow-hidden max-h-[90vh]">
            <div className="bg-gradient-to-r from-[#272c39] to-[#394254] p-3 sm:p-4 text-white flex justify-between items-center">
              <h3 className="text-base sm:text-lg font-semibold flex items-center"><FaFileContract className="mr-2" size={16} /><span className="hidden sm:inline">{t('customerDashboard.contractsAndProducts')}</span><span className="sm:hidden">{t('customerDashboard.contracts')}</span></h3>
              <button onClick={() => setIsContractModalOpen(false)} className="text-white/80 hover:text-white"><MdClose size={18} /></button>
            </div>
            
            {/* Desktop Layout */}
            <div className="hidden lg:flex h-[500px] overflow-hidden">
              <div className="w-1/3 border-r dark:border-gray-700 p-4 overflow-y-auto">
                <h4 className="text-lg font-medium mb-4 text-gray-700 dark:text-white">{t('customerDashboard.yourContracts')}</h4>
                {isLoading && !contracts.length ? <div className="flex justify-center items-center h-32"><div className="inline-block h-6 w-6 rounded-full border-4 border-gray-300 border-t-blue-600 animate-spin"></div></div> : contracts.length > 0 ? (
                  <div className="space-y-3">
                    {contracts.map(contract => (
                      <div key={contract.id} onClick={() => handleContractClick(contract)} className={`p-3 rounded-md border hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${selectedContract && selectedContract.id === contract.id ? 'bg-blue-50 dark:bg-blue-900 border-blue-300 dark:border-blue-600' : 'border-gray-200 dark:border-gray-600'}`}>
                        <div className="flex items-center">
                          <FaHandshake className="text-blue-500 dark:text-blue-400 mr-3" size={18} />
                          <div>
                            <h5 className="font-medium dark:text-white">{contract.contract_name}</h5>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{new Date(contract.start_date).toLocaleDateString()} - {new Date(contract.end_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div className="text-center py-10 text-gray-500 dark:text-gray-400"><FaFileContract size={40} className="mx-auto mb-3 text-gray-400" /><p>{t('customerDashboard.noContractsYet')}</p></div>}
              </div>
              
              <div className="w-1/3 border-r dark:border-gray-700 p-4 overflow-y-auto">
                <h4 className="text-lg font-medium mb-4 text-gray-700 dark:text-white">{t('customerDashboard.products')}</h4>
                {selectedContract ? (isLoading && !contractProducts.length ? <div className="flex justify-center items-center h-32"><div className="inline-block h-6 w-6 rounded-full border-4 border-gray-300 border-t-blue-600 animate-spin"></div></div> : contractProducts.length > 0 ? (
                  <div className="space-y-3">
                    {contractProducts.map(product => (
                      <div key={product.id} onClick={() => handleProductSelect(product)} className={`p-3 rounded-md border hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${selectedProduct && selectedProduct.id === product.id ? 'bg-green-50 dark:bg-green-900 border-green-300 dark:border-green-600' : 'border-gray-200 dark:border-gray-600'}`}>
                        <div className="flex items-center">
                          <FaClipboardList className="text-green-500 dark:text-green-400 mr-3" size={18} />
                          <h5 className="font-medium dark:text-white">{product.product_name}</h5>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div className="text-center py-10 text-gray-500 dark:text-gray-400"><FaClipboardList size={40} className="mx-auto mb-3 text-gray-400" /><p>{t('customerDashboard.noProductsForContract')}</p></div>) : <div className="text-center py-10 text-gray-500 dark:text-gray-400"><MdInfo size={40} className="mx-auto mb-3 text-gray-400" /><p>{t('customerDashboard.selectContractToViewProducts')}</p></div>}
              </div>
              
              <div className="w-1/3 p-4 overflow-y-auto">
                <h4 className="text-lg font-medium mb-4 text-gray-700 dark:text-white">{t('customerDashboard.slaDefinitions')}</h4>
                {selectedProduct ? (isLoading && !productSLAs.length ? <div className="flex justify-center items-center h-32"><div className="inline-block h-6 w-6 rounded-full border-4 border-gray-300 border-t-blue-600 animate-spin"></div></div> : productSLAs.length > 0 ? (
                  <div className="space-y-4">
                    {productSLAs.map(sla => (
                      <div key={sla.id} className="p-4 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                        <h5 className="font-medium text-gray-800 dark:text-white mb-2">{sla.sla_name}</h5>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="text-gray-600 dark:text-gray-400">{t('customerDashboard.priority')}:</div><div className="dark:text-white">{sla.sla_severity}</div>
                          <div className="text-gray-600 dark:text-gray-400">{t('customerDashboard.responseTime')}:</div><div className="dark:text-white">{sla.response_time} {t('customerDashboard.hours')}</div>
                          <div className="text-gray-600 dark:text-gray-400">{t('customerDashboard.resolutionTime')}:</div><div className="dark:text-white">{sla.resolution_time} {t('customerDashboard.hours')}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div className="text-center py-10 text-gray-500 dark:text-gray-400"><FaHandshake size={40} className="mx-auto mb-3 text-gray-400" /><p>{t('customerDashboard.noSlaForProduct')}</p></div>) : <div className="text-center py-10 text-gray-500 dark:text-gray-400"><MdInfo size={40} className="mx-auto mb-3 text-gray-400" /><p>{t('customerDashboard.selectProductToViewSla')}</p></div>}
              </div>
            </div>

            {/* Mobile Layout - Compact */}
            <div className="lg:hidden flex flex-col max-h-[70vh]">
              {/* Mobile Navigation Tabs */}
              <div className="flex border-b dark:border-gray-700">
                <button 
                  className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                    !selectedContract ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400'
                  }`}
                  onClick={() => {
                    setSelectedContract(null);
                    setSelectedProduct(null);
                    setProductSLAs([]);
                  }}
                >
                  {t('customerDashboard.yourContracts')}
                </button>
                <button 
                  className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                    selectedContract && !selectedProduct ? 'border-green-500 text-green-600 dark:text-green-400' : 'border-transparent text-gray-500 dark:text-gray-400'
                  }`}
                  onClick={() => {
                    if (selectedContract) {
                      setSelectedProduct(null);
                      setProductSLAs([]);
                    }
                  }}
                  disabled={!selectedContract}
                >
                  {t('customerDashboard.products')}
                </button>
                <button 
                  className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                    selectedProduct ? 'border-purple-500 text-purple-600 dark:text-purple-400' : 'border-transparent text-gray-500 dark:text-gray-400'
                  }`}
                  disabled={!selectedProduct}
                >
                  SLA
                </button>
              </div>

              {/* Mobile Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* Contracts Tab */}
                {!selectedContract && (
                  <div>
                    <h4 className="text-base font-medium mb-3 text-gray-700 dark:text-white">{t('customerDashboard.yourContracts')}</h4>
                    {isLoading && !contracts.length ? (
                      <div className="flex justify-center items-center h-24">
                        <div className="inline-block h-5 w-5 rounded-full border-4 border-gray-300 border-t-blue-600 animate-spin"></div>
                      </div>
                    ) : contracts.length > 0 ? (
                      <div className="space-y-2">
                        {contracts.map(contract => (
                          <div key={contract.id} onClick={() => handleContractClick(contract)} className="p-3 rounded-md border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                            <div className="flex items-center">
                              <FaHandshake className="text-blue-500 dark:text-blue-400 mr-2" size={16} />
                              <div className="flex-1 min-w-0">
                                <h5 className="font-medium dark:text-white text-sm truncate">{contract.contract_name}</h5>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{new Date(contract.start_date).toLocaleDateString()} - {new Date(contract.end_date).toLocaleDateString()}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <FaFileContract size={32} className="mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">{t('customerDashboard.noContractsYet')}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Products Tab */}
                {selectedContract && !selectedProduct && (
                  <div>
                    <div className="flex items-center mb-3">
                      <button 
                        onClick={() => {
                          setSelectedContract(null);
                          setSelectedProduct(null);
                          setProductSLAs([]);
                        }}
                        className="mr-2 text-blue-500 hover:text-blue-600"
                      >
                        ←
                      </button>
                      <h4 className="text-base font-medium text-gray-700 dark:text-white">{t('customerDashboard.products')}</h4>
                    </div>
                    {isLoading && !contractProducts.length ? (
                      <div className="flex justify-center items-center h-24">
                        <div className="inline-block h-5 w-5 rounded-full border-4 border-gray-300 border-t-blue-600 animate-spin"></div>
                      </div>
                    ) : contractProducts.length > 0 ? (
                      <div className="space-y-2">
                        {contractProducts.map(product => (
                          <div key={product.id} onClick={() => handleProductSelect(product)} className="p-3 rounded-md border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                            <div className="flex items-center">
                              <FaClipboardList className="text-green-500 dark:text-green-400 mr-2" size={16} />
                              <h5 className="font-medium dark:text-white text-sm">{product.product_name}</h5>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <FaClipboardList size={32} className="mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">{t('customerDashboard.noProductsForContract')}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* SLA Tab */}
                {selectedProduct && (
                  <div>
                    <div className="flex items-center mb-3">
                      <button 
                        onClick={() => {
                          setSelectedProduct(null);
                          setProductSLAs([]);
                        }}
                        className="mr-2 text-green-500 hover:text-green-600"
                      >
                        ←
                      </button>
                      <h4 className="text-base font-medium text-gray-700 dark:text-white">{t('customerDashboard.slaDefinitions')}</h4>
                    </div>
                    {isLoading && !productSLAs.length ? (
                      <div className="flex justify-center items-center h-24">
                        <div className="inline-block h-5 w-5 rounded-full border-4 border-gray-300 border-t-blue-600 animate-spin"></div>
                      </div>
                    ) : productSLAs.length > 0 ? (
                      <div className="space-y-3">
                        {productSLAs.map(sla => (
                          <div key={sla.id} className="p-3 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                            <h5 className="font-medium text-gray-800 dark:text-white mb-2 text-sm">{sla.sla_name}</h5>
                            <div className="space-y-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">{t('customerDashboard.priority')}:</span>
                                <span className="dark:text-white">{sla.sla_severity}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">{t('customerDashboard.responseTime')}:</span>
                                <span className="dark:text-white">{sla.response_time} {t('customerDashboard.hours')}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">{t('customerDashboard.resolutionTime')}:</span>
                                <span className="dark:text-white">{sla.resolution_time} {t('customerDashboard.hours')}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <FaHandshake size={32} className="mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">{t('customerDashboard.noSlaForProduct')}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-3 sm:p-4 border-t dark:border-gray-700 flex justify-end">
              <button onClick={() => setIsContractModalOpen(false)} className="px-3 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors text-sm">{t('customerDashboard.close')}</button>
            </div>
          </div>
        </div>
      )}
    </CustomerLayout>
  );
};

export default CustomerDashboardPage;